/**
 * Next best action on the widget (spec-langchain-native-runtime 09b, re-cut by 09a / W15).
 *
 * A reply may carry ONE suggested next step (PRD FR-03). It reaches the widget two ways:
 *  - live: `record_update_stream { recordId, conversationId, suggestions: [{ id, label, prompt, reason }] }`
 *    (protocol ≥ 2, no `chunk` / `done`), which may land slightly before or after the stream's
 *    `done` — so it is kept per `recordId`, never on the stream entry;
 *  - persisted: `record.suggestedActions` on the history read and on `record_update`.
 *
 * A chip click sends ONLY the chip's `prompt` as an ordinary visitor message (W15): chat-service
 * recognises acceptance by that exact text. Nothing else rides on the body.
 */
import { isServerRecordId } from "./feedback";

/** PRD FR-03 (owner 2026-09-11): at most one chip per reply. */
export const MAX_SUGGESTIONS = 1;

/** Reply types a chip may hang off: an ordinary text answer, never a collector or a gallery. */
const CHIP_REPLY_TYPES = new Set(["msg", "assistant"]);

const text = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * The renderable suggestions of a raw value: an array of objects with a non-empty `label` and
 * `prompt`, at most {@link MAX_SUGGESTIONS}, reduced to the customer-facing fields. Anything else
 * (absent, not an array, malformed entries) → `[]`.
 */
export function sanitizeSuggestions(raw) {
	if (!Array.isArray(raw)) return [];
	const out = [];
	for (const a of raw) {
		if (!a || typeof a !== "object") continue;
		const label = text(a.label);
		const prompt = text(a.prompt);
		if (!label || !prompt) continue;
		out.push({ id: text(a.id) || prompt, label, prompt, reason: text(a.reason) });
		if (out.length === MAX_SUGGESTIONS) break;
	}
	return out;
}

/**
 * What a record offers: the persisted `record.suggestedActions` first (authoritative), then the
 * live `suggestions` this session received for the record.
 */
export function resolveRecordSuggestions(record, live) {
	const persisted = sanitizeSuggestions(record?.suggestedActions);
	return persisted.length > 0 ? persisted : sanitizeSuggestions(live);
}

/**
 * The id of the record whose chips may render: the LAST visible record, when it is a finished,
 * persisted agent text reply. Only the latest agent message's chip can be accepted (09a), and a
 * visitor message sent after it (its local echo is a later record) retires the chips at once.
 */
export function chipRecordId(records) {
	if (!Array.isArray(records) || records.length === 0) return null;
	const last = records[records.length - 1];
	const reply = last?.reply;
	if (!reply || reply.streaming === true || last.pending) return null;
	if (!CHIP_REPLY_TYPES.has(reply.type)) return null;
	return isServerRecordId(last._id) ? last._id : null;
}
